/**
 * StorageStatusBar
 *
 * Floating pill fixed at the bottom-center of the right panel (positions area).
 * Aggregates stats across ALL loaded storages (not just the active tab).
 *
 * Positioning trick: uses `position: fixed` with `left = panelCenter` computed
 * by a ResizeObserver in WarehouseStorageManager that measures the right panel's
 * mid-point in viewport coordinates. This keeps the pill centered on the
 * positions panel regardless of the left menu width.
 *
 * Props:
 *   stats            – { total, occupied, free, pending } — aggregated counts
 *   selectedResource – resource being placed (false if none); shows "Placing: …" indicator
 *   onCancelSelection – () => void — clears the selected resource
 *   movingResource   – { resource, fromPositionId, fromStorageId } or false
 *   onCancelMove     – () => void — cancels the active move operation
 *   panelCenter      – CSS left value (e.g. "1024px") computed from ResizeObserver
 */
import { Box, Typography, Stack, Divider, IconButton, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { CancelRounded } from '@mui/icons-material'
import { PENDING_COLOR, dot } from './warehouseStorageStyles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

export default function StorageStatusBar ({ stats, currentStorageName, currentStorageStats, selectedResource, onCancelSelection, movingResource, onCancelMove, panelCenter }) {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const globalItems = [
    { label: t_('Total'), value: stats.total, color: '#888' },
    { label: t_('Occupied'), value: stats.occupied, color: '#4caf50' },
    { label: t_('Free'), value: stats.free, color: '#2196f3' },
    { label: t_('Pending'), value: stats.pending, color: stats.pending > 0 ? PENDING_COLOR : '#555' }
  ]

  return (
    <Box sx={{
      position: 'fixed',
      bottom: 20,
      left: panelCenter || '50vw',
      transform: 'translateX(-50%)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2.5,
      px: 3,
      py: 1.2,
      borderRadius: 10,
      bgcolor: 'rgba(22,22,22,0.88)',
      backdropFilter: 'blur(14px)',
      border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)',
      zIndex: 10,
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap'
    }}
    >
      {/* Global totals */}
      {globalItems.map(({ label, value, color }) => (
        <Stack key={label} direction='row' alignItems='center' spacing={1}>
          <Box sx={dot(8, color)} />
          <Typography sx={{ fontSize: '0.88rem', color: '#666' }}>{label}</Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color }}>{value}</Typography>
        </Stack>
      ))}

      {/* Current storage breakdown */}
      {currentStorageName && currentStorageStats && (
        <>
          <Divider orientation='vertical' flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 0.5 }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#555', fontWeight: 600 }}>
            {currentStorageName}
          </Typography>
          {[
            { label: t_('Occupied'), value: currentStorageStats.occupied, color: '#4caf50' },
            { label: t_('Free'), value: currentStorageStats.free, color: '#2196f3' }
          ].map(({ label, value, color }) => (
            <Stack key={label} direction='row' alignItems='center' spacing={0.8}>
              <Box sx={dot(7, color)} />
              <Typography sx={{ fontSize: '0.82rem', color: '#666' }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color }}>{value}</Typography>
            </Stack>
          ))}
        </>
      )}

      {selectedResource && (
        <>
          <Divider orientation='vertical' flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 0.5 }} />
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={{ ...dot(9, accent), boxShadow: `0 0 8px ${alpha(accent, 0.7)}` }} />
            <Typography sx={{ fontSize: '0.88rem', color: accent, fontWeight: 500 }}>
              {t_('Placing')}: {selectedResource.name}
            </Typography>
            <IconButton size='small' onClick={onCancelSelection} sx={{ color: '#666', p: 0.3 }}>
              <CancelRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </>
      )}

      {movingResource && (
        <>
          <Divider orientation='vertical' flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 0.5 }} />
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={{ ...dot(9, '#ff9800'), boxShadow: '0 0 8px rgba(255,152,0,0.7)' }} />
            <Typography sx={{ fontSize: '0.88rem', color: '#ff9800', fontWeight: 500 }}>
              {t_('Moving')}: {movingResource.resource?.name || movingResource.resource?.code}
            </Typography>
            <IconButton size='small' onClick={onCancelMove} sx={{ color: '#666', p: 0.3 }}>
              <CancelRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </>
      )}
    </Box>
  )
}
